import { useInfiniteQuery } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { reviewKeys } from '@/lib/queryKeys';
import { reviewsV2Endpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api';
import { isBadRequest } from '@/lib/api/errors';
import type { UnifiedReviewListParams, UnifiedReviewListResponse } from '@/types/review';

/**
 * Polls the unified reviews dashboard list (`GET /v2/reviews`).
 *
 * Spec §12 calls for 30s background polling so dispatcher dashboards stay
 * fresh without manual refresh. We disable refetch-on-window-focus so the
 * polling cadence is the single source of refresh truth.
 */
export function useUnifiedReviews(params: UnifiedReviewListParams = {}) {
  return useApiQuery<UnifiedReviewListResponse>({
    queryKey: reviewKeys.list(params as Record<string, unknown>),
    queryFn: async ({ signal }) => {
      const raw = await apiGet<UnifiedReviewListResponse>(
        operationsClient,
        reviewsV2Endpoints.list(),
        { params: serializeParams(params), signal },
      );
      return {
        data: raw.data ?? [],
        nextCursor: raw.nextCursor,
      };
    },
    refetchInterval: (query) => (isBadRequest(query.state.error) ? false : 30_000),
    refetchOnWindowFocus: false,
    showErrorToast: false,
  });
}

/**
 * Axios serializes arrays by default as `?key=a&key=b` which class-validator's
 * `@IsArray()` happily accepts on the backend, but only when the param appears
 * once. We normalize empty arrays to `undefined` so they don't appear at all
 * (otherwise `?platforms=` would arrive as `''` and fail validation).
 */
function serializeParams(params: UnifiedReviewListParams): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      out[key] = value;
    } else if (typeof value === 'string' && value.length === 0) {
      continue;
    } else {
      out[key] = value;
    }
  }
  return out;
}

/**
 * The same list, accumulated across pages, for the infinite-scrolling table.
 *
 * `useUnifiedReviews` keys the cache on the cursor, so advancing a page
 * REPLACED the rows on screen instead of extending them — "Load more" swapped
 * page 1 for page 2 and the list stayed 25 long, which reads as a dead button.
 * An infinite query holds the pages together, which is also what makes
 * scroll-to-load possible at all.
 */
export function useInfiniteUnifiedReviews(params: UnifiedReviewListParams = {}) {
  const { cursor: _ignoredCursor, ...rest } = params;
  return useInfiniteQuery({
    // The cursor is deliberately NOT in the key: it identifies a page within
    // this list, not a different list. Keying on it is what made every page a
    // separate cache entry in the first place.
    queryKey: [...reviewKeys.lists(), 'infinite', rest as Record<string, unknown>],
    queryFn: async ({
      pageParam,
      signal,
    }: {
      pageParam: string | undefined;
      signal?: AbortSignal;
    }) => {
      const raw = await apiGet<UnifiedReviewListResponse>(
        operationsClient,
        reviewsV2Endpoints.list(),
        { params: serializeParams({ ...rest, cursor: pageParam }), signal },
      );
      return { data: raw.data ?? [], nextCursor: raw.nextCursor };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: UnifiedReviewListResponse) => last.nextCursor ?? undefined,
    // Both of these carry over from `useUnifiedReviews`, which this hook
    // replaced as the table's source: spec §12's 30s polling, and the toast
    // suppression that `useApiQuery` used to translate from
    // `showErrorToast: false`. Raw `useInfiniteQuery` gets neither for free,
    // so without them the table silently stopped refreshing and started
    // raising a Sonner toast on top of its own inline error state.
    // Polling stops once the server has refused the request — see
    // `isBadRequest`. Retrying is handled by the shared policy in
    // `shouldRetry`, which declines a 400 for every query in the app; this
    // half has to be local because `refetchInterval` has no shared default.
    // Without it a rejected filter costs a request every 30s, indefinitely,
    // behind an error the operator cannot clear. `useReviewStories` gives its
    // cursor 400s the same treatment.
    refetchInterval: (query) => (isBadRequest(query.state.error) ? false : 30_000),
    meta: { suppressToast: true },
    refetchOnWindowFocus: false,
    // Declared once, not inline: React Query caches a select result against the
    // function identity it came from, and a new closure each render discards it.
    select: selectUnifiedPages,
  });
}

/** Exported for its own test — the whole of what the table finally renders. */
export function selectUnifiedPages(data: { pages: UnifiedReviewListResponse[] }) {
  return {
    data: data.pages.flatMap((page) => page.data ?? []),
    // The newest page owns the answer to "is there more"; earlier pages all
    // carried a cursor and would claim there is.
    nextCursor: data.pages[data.pages.length - 1]?.nextCursor,
  };
}
